export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_PREFERENCE_KEY = 'beam:color-theme';

export const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

export const getSavedThemePreference = (
  storage?: Pick<Storage, 'getItem'>,
): ThemePreference => {
  if (!storage) return 'system';

  try {
    const savedPreference = storage.getItem(THEME_PREFERENCE_KEY);
    return isThemePreference(savedPreference) ? savedPreference : 'system';
  } catch {
    return 'system';
  }
};

export const saveThemePreference = (
  storage: Pick<Storage, 'setItem'> | undefined,
  preference: ThemePreference,
) => {
  try {
    storage?.setItem(THEME_PREFERENCE_KEY, preference);
  } catch {
    // The theme still applies for this session when storage is blocked.
  }
};

export const getSystemTheme = (
  media?: Pick<MediaQueryList, 'matches'>,
): ResolvedTheme => (media?.matches ? 'dark' : 'light');

export const resolveThemePreference = (
  preference: ThemePreference,
  systemTheme: ResolvedTheme,
): ResolvedTheme => (preference === 'system' ? systemTheme : preference);

export const applyThemeToDocument = (
  resolvedTheme: ResolvedTheme,
  root?: HTMLElement | null,
) => {
  if (!root) return;
  root.dataset.theme = resolvedTheme;
  root.style.setProperty('color-scheme', resolvedTheme);
};

export const applyInitialTheme = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const preference = getSavedThemePreference(window.localStorage);
  const media = window.matchMedia?.('(prefers-color-scheme: dark)');
  applyThemeToDocument(
    resolveThemePreference(preference, getSystemTheme(media)),
    document.documentElement,
  );
};
